import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from '../utils/toast';

const CustomPageSubmissions = () => {
  const { pageId } = useParams();
  const { checkPermission } = useAuth();
  const navigate = useNavigate();
  
  const [customPage, setCustomPage] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  const canView = checkPermission('View Custom Page Submissions');
  const canCreate = checkPermission('Create Custom Page Submission');
  const canUpdate = checkPermission('Update Custom Page Submission');
  const canDelete = checkPermission('Delete Custom Page Submission');

  useEffect(() => {
    fetchCustomPage();
    fetchSubmissions();
  }, [pageId, pagination.page, searchTerm, selectedStatus]);

  const fetchCustomPage = async () => {
    try {
      const response = await api.get(`/custom-pages/${pageId}`);
      if (response.data.success) {
        setCustomPage(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching custom page:', error);
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });

      if (searchTerm) params.append('search', searchTerm);
      if (selectedStatus) params.append('status', selectedStatus);

      const response = await api.get(`/custom-pages/${pageId}/submissions?${params}`);
      
      if (response.data.success) {
        setSubmissions(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages
        }));
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (submission) => {
    try {
      const response = await api.get(`/custom-pages/submissions/${submission._id}`);
      if (response.data.success) {
        setSelectedSubmission(response.data.data);
        setShowDetails(true);
      }
    } catch (error) {
      console.error('Error fetching submission details:', error);
      toast.error('Failed to fetch submission details');
    }
  };

  const handleStatusChange = async (submissionId, newStatus) => {
    try {
      const response = await api.put(`/custom-pages/submissions/${submissionId}`, {
        status: newStatus
      });
      
      if (response.data.success) {
        toast.success('Status updated successfully');
        fetchSubmissions();
        if (selectedSubmission && selectedSubmission._id === submissionId) {
          setSelectedSubmission(response.data.data);
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) {
      return;
    }

    try {
      const response = await api.delete(`/custom-pages/submissions/${submissionId}`);
      
      if (response.data.success) {
        toast.success('Submission deleted successfully');
        fetchSubmissions();
        if (selectedSubmission && selectedSubmission._id === submissionId) {
          setShowDetails(false);
          setSelectedSubmission(null);
        }
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderFieldValue = (field, value) => {
    if (!value) return '-';

    switch (field.type) {
      case 'upload':
        if (Array.isArray(value) && value.length > 0) {
          return (
            <div className="space-y-1">
              {value.map((file, index) => (
                <div key={index} className="text-sm text-blue-600 hover:text-blue-800">
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    {file.name}
                  </a>
                </div>
              ))}
            </div>
          );
        }
        return 'No files uploaded';

      case 'masterdata':
        if (value && typeof value === 'object') {
          return value.clientName || 'N/A';
        }
        return value || '-';

      case 'user':
        if (value && typeof value === 'object') {
          return `${value.firstName} ${value.lastName}`;
        }
        return value || '-';

      case 'date':
        return formatDate(value);

      case 'multitext':
        return value.length > 100 ? `${value.substring(0, 100)}...` : value;

      default:
        return value;
    }
  };

  if (loading && submissions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!customPage) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Page Not Found</h2>
        <p className="text-gray-600">The requested page could not be found.</p>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to view submissions.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {customPage.name} - Submissions
          </h1>
          <p className="text-gray-600">View and manage form submissions</p>
        </div>
        {canCreate && (
          <button
            onClick={() => navigate(`/custom-pages/${pageId}`)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Create New Submission
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search submissions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Submissions List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deadline
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {submissions.map((submission) => (
                <tr key={submission._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {submission.submittedBy?.firstName} {submission.submittedBy?.lastName}
                    </div>
                    <div className="text-sm text-gray-500">
                      {submission.submittedBy?.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(submission.status)}`}>
                      {submission.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(submission.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {submission.deadline ? formatDate(submission.deadline) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleViewDetails(submission)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View
                    </button>
                    {canUpdate && (
                      <select
                        value={submission.status}
                        onChange={(e) => handleStatusChange(submission._id, e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="draft">Draft</option>
                        <option value="submitted">Submitted</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="completed">Completed</option>
                      </select>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(submission._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {submissions.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className="text-gray-500">No submissions found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-700">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm text-gray-700">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Submission Details Modal */}
      {showDetails && selectedSubmission && (
        <SubmissionDetailsModal
          submission={selectedSubmission}
          customPage={customPage}
          onClose={() => {
            setShowDetails(false);
            setSelectedSubmission(null);
          }}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      )}
    </div>
  );
};

// Submission Details Modal Component
const SubmissionDetailsModal = ({ 
  submission, 
  customPage, 
  onClose, 
  onStatusChange, 
  onDelete,
  canUpdate,
  canDelete 
}) => {
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await api.post(`/custom-pages/submissions/${submission._id}/comments`, {
        text: newComment.trim()
      });
      
      if (response.data.success) {
        setNewComment('');
        // Refresh submission data
        const updatedResponse = await api.get(`/custom-pages/submissions/${submission._id}`);
        if (updatedResponse.data.success) {
          // Update the submission in parent component
          onStatusChange(submission._id, submission.status); // This will trigger a refresh
        }
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderFieldValue = (field, value) => {
    if (!value) return '-';

    switch (field.type) {
      case 'upload':
        if (Array.isArray(value) && value.length > 0) {
          return (
            <div className="space-y-1">
              {value.map((file, index) => (
                <div key={index} className="text-sm text-blue-600 hover:text-blue-800">
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    {file.name}
                  </a>
                </div>
              ))}
            </div>
          );
        }
        return 'No files uploaded';

      case 'masterdata':
        if (value && typeof value === 'object') {
          return value.clientName || 'N/A';
        }
        return value || '-';

      case 'user':
        if (value && typeof value === 'object') {
          return `${value.firstName} ${value.lastName}`;
        }
        return value || '-';

      case 'date':
        return formatDate(value);

      case 'multitext':
        return (
          <div className="whitespace-pre-wrap text-sm">
            {value}
          </div>
        );

      default:
        return value;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Submission Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6">
          {/* Submission Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                submission.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                submission.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                submission.status === 'approved' ? 'bg-green-100 text-green-800' :
                submission.status === 'rejected' ? 'bg-red-100 text-red-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                {submission.status}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Submitted By</label>
              <p className="text-sm text-gray-900">
                {submission.submittedBy?.firstName} {submission.submittedBy?.lastName}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Submitted Date</label>
              <p className="text-sm text-gray-900">{formatDate(submission.createdAt)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Deadline</label>
              <p className="text-sm text-gray-900">
                {submission.deadline ? formatDate(submission.deadline) : '-'}
              </p>
            </div>
          </div>

          {/* Form Data */}
          <div>
            <h3 className="text-lg font-medium mb-4">Form Data</h3>
            <div className="space-y-4">
              {customPage.fields.map((field, index) => (
                <div key={index} className="border-b border-gray-200 pb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                  </label>
                  <div className="text-sm text-gray-900">
                    {renderFieldValue(field, submission.data[field.name])}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Comments */}
          <div>
            <h3 className="text-lg font-medium mb-4">Comments</h3>
            <div className="space-y-4">
              {submission.comments?.map((comment, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {comment.createdBy?.firstName} {comment.createdBy?.lastName}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{comment.text}</p>
                </div>
              ))}
              
              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} className="space-y-2">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={submittingComment || !newComment.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {submittingComment ? 'Adding...' : 'Add Comment'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            {canUpdate && (
              <select
                value={submission.status}
                onChange={(e) => onStatusChange(submission._id, e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="completed">Completed</option>
              </select>
            )}
            {canDelete && (
              <button
                onClick={() => onDelete(submission._id)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomPageSubmissions; 